import React, { useEffect, useState, useRef } from "react";
import { v4 as uuidv4 } from "uuid";
import {
  formatBoldText,
  formatStreamingHtmlResponse,
} from "../../../services/commonService";
import {
  getChatWindowResponse,
  getLiveChatResponse,
} from "../../../services/accIqService";

export default function RevenueChatByNode({
  USER_COMPANY,
  COMPANY_NAME,
  table_name,
}) {
  const [chat, setChat] = useState([]);
  const [input, setInput] = useState("");
  const [showQuestions, setShowQuestions] = useState(true);
  const [questions, setQuestions] = useState([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const chatEndRef = useRef(null);

  // Scroll chat to bottom when updated
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chat]);

  // Fetch pre-built Revenue Q&A
  useEffect(() => {
    if (!USER_COMPANY || !COMPANY_NAME || !table_name) return;

    const fetchQAData = async () => {
      try {
        const queryParams = new URLSearchParams({
          USER_COMPANY,
          COMPANY_NAME,
          table_name,
        }).toString();

        const response = await getChatWindowResponse(queryParams);
        if (!response.ok)
          throw new Error(`HTTP error! status: ${response.status}`);

        const data = await response.json();

        const qna =
          data?.response?.[0]?.response?.revenue_qna?.questions ||
          data?.response?.[0]?.response?.competitive_intelligence_qna
            ?.questions ||
          [];

        setQuestions(qna);
      } catch (error) {
        console.error("Error fetching Revenue Q&A:", error);
      }
    };

    fetchQAData();
  }, [USER_COMPANY, COMPANY_NAME, table_name]);

  const handleQuestionClick = (qObj) => {
    setShowQuestions(false);
    setChat((prev) => [
      ...prev,
      { id: uuidv4(), from: "user", type: "question", text: qObj.question },
      { id: uuidv4(), from: "bot", type: "answer", blocks: qObj.answer },
    ]);
  };

  // Live Streaming Chat Handler (same as AIChat)
  const handleSend = async () => {
    if (!input.trim()) return;

    const userMessage = { id: uuidv4(), from: "user", text: input };
    const botMessageId = uuidv4();

    // Add user + placeholder
    setChat((prev) => [
      ...prev,
      userMessage,
      { id: botMessageId, from: "bot", text: "", isSecondApi: true },
    ]);

    setInput("");
    setIsStreaming(true);

    try {
      const queryParams = { user_input: input };
      const response = await getLiveChatResponse(queryParams);

      if (!response.ok || !response.body)
        throw new Error("Failed to stream response");

      const reader = response.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let fullText = "";
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");

        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed) continue;
          if (trimmed === "data: [DONE]") {
            buffer = "";
            break;
          }
          if (trimmed.startsWith("data: ")) {
            const content = trimmed.replace("data: ", "");
            fullText += content;
            setChat((prev) =>
              prev.map((msg) =>
                msg.id === botMessageId ? { ...msg, text: fullText } : msg
              )
            );
          }
        }
        buffer = lines[lines.length - 1].endsWith("data: [DONE]")
          ? ""
          : lines[lines.length - 1];
      }
    } catch (err) {
      console.error("Streaming error:", err);
      setChat((prev) => [
        ...prev,
        {
          id: uuidv4(),
          from: "bot",
          text: "An error occurred while getting a response.",
          isSecondApi: true,
        },
      ]);
    }

    setIsStreaming(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") handleSend();
  };

  return (
    <div className="ai_insight_wrapper">
      <div className="ai_insight_body">
        {/* Pre-Built Questions */}
        {showQuestions && questions.length > 0 && (
          <div className="ai_list_block">
            <h5 className="ai_list_head">Revenue Analysis Questions:</h5>
            <ul className="ai_list_ul">
              {questions.map((qObj) => (
                <li
                  key={qObj.id}
                  className="ai_list_li"
                  onClick={() => handleQuestionClick(qObj)}
                  style={{ cursor: "pointer" }}
                >
                  • {qObj.question}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Chat Section */}
        <div className="chat_box_ai">
          {chat.map((item) =>
            item.from === "user" ? (
              <div key={item.id} className="system_ai_block" ref={chatEndRef}>
                <h5 className="subname">{USER_COMPANY || "You"}</h5>
                <div className="system_box_right">
                  <div className="system_text_right">{item.text}</div>
                </div>
              </div>
            ) : (
              <div key={item.id} className="user_block">
                <h5 className="subname">AI Revenue Analysis</h5>
                <div className="user_box_left">
                  <div className="insight_block">
                    <div className="row">
                      {/* Why Block */}
                      <div className="col-md-12" ref={chatEndRef}>
                        <div
                          className="card_block positive mb-2"
                          style={{ borderLeft: "1px solid #15803D29" }}
                        >
                          {!item.isSecondApi && (
                            <h6 className="head green_head">
                              Why is this happening?
                            </h6>
                          )}
                          <ul className="list_card_ul">
                            {item.blocks?.why?.length ? (
                              item.blocks.why.map((txt, i) => (
                                <li key={i} className="list_card_li">
                                  <span className="list_card_text">
                                    {formatBoldText(txt)}
                                  </span>
                                </li>
                              ))
                            ) : (
                              <span
                                className="chat_response"
                                style={{
                                  fontFamily: "Inter",
                                  fontWeight: "400",
                                  fontSize: "12px",
                                  lineHeight: "17.4px",
                                  marginBottom: "10px",
                                  color: "#1e1c25",
                                }}
                                dangerouslySetInnerHTML={{
                                  __html: formatStreamingHtmlResponse(item.text),
                                }}
                              />
                            )}
                          </ul>
                        </div>
                      </div>

                      {/* Risks Block */}
                      {!item.isSecondApi && (
                        <div className="col-md-12">
                          <div
                            className="card_block negative mb-2"
                            style={{ borderLeft: "1px solid #B91C1C29" }}
                          >
                            <h6 className="head negative_head">
                              Revenue Risks
                            </h6>
                            <ul className="list_card_ul">
                              {item.blocks?.vulnerabilities?.length ? (
                                item.blocks.vulnerabilities.map((txt, i) => (
                                  <li key={i} className="list_card_li">
                                    <span className="list_card_text">{txt}</span>
                                  </li>
                                ))
                              ) : (
                                <li className="list_card_li">
                                  <span className="list_card_text">
                                    No major risks identified
                                  </span>
                                </li>
                              )}
                            </ul>
                          </div>
                        </div>
                      )}

                      {/* Implications Block */}
                      {!item.isSecondApi && (
                        <div className="col-md-12">
                          <div
                            className="card_block neutral mb-2"
                            style={{ borderLeft: "1px solid #2563EB29" }}
                          >
                            <h6 className="head neutral_head">
                              Strategic Implications
                            </h6>
                            <ul className="list_card_ul">
                              {item.blocks?.implications?.length ? (
                                item.blocks.implications.map((txt, i) => (
                                  <li key={i} className="list_card_li">
                                    <span className="list_card_text">{txt}</span>
                                  </li>
                                ))
                              ) : (
                                <li className="list_card_li">
                                  <span className="list_card_text">
                                    No implications found
                                  </span>
                                </li>
                              )}
                            </ul>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )
          )}
        </div>
      </div>

      {isStreaming && <div className="streaming-placeholder">...</div>}

      {/* Input */}
      <div className="chat_bottom_block">
        <h5 className="chat_bottom_head">Ask Your Own Question:</h5>
        <div className="chat_message_input_box">
          <input
            className="message-input-field"
            type="text"
            placeholder={`Ask anything about ${COMPANY_NAME || "the company"}'s revenue performance...`}
            maxLength="100"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyPress}
          />
          <div className="send-message-button" onClick={handleSend}>
            <img src="/img/send_chat.png" alt="icon" className="send_icon" />
          </div>
        </div>
      </div>
    </div>
  );
}
